import {
  DateQuery,
  Paginated,
  convertToUrlParams,
} from "@shared/services/common";
import { TwinBase, TwinResponse } from "@shared/services/twinManagement";
import axios, { AxiosPromise, AxiosRequestConfig, AxiosResponse } from "axios";
import { type MaintenanceTypes } from "@inctec/smx-ui-client";

export async function submitTicket(
  id: string,
  comment: string,
  longComment: string,
) {
  return axios.post(`/api/tm/tickets/${id}/submit`, { comment, longComment });
}

export async function rejectTicket(id: string, reason: string) {
  return axios.post(`/api/tm/tickets/${id}/reject`, { reason });
}

export async function planTicket(
  id: string,
  comment: string,
  longComment: string,
  assignee: string,
  startDate: string,
  dueDate: string,
) {
  return axios.post(`/api/tm/tickets/${id}/plan`, {
    comment,
    longComment,
    assignee,
    startDate,
    dueDate,
  });
}

export async function inProgressTicket(id: string) {
  return axios.post(`/api/tm/tickets/${id}/in-process`);
}

export async function validateTicket(
  id: string,
  validated: boolean,
  comment: string,
) {
  return axios.post(`/api/tm/tickets/${id}/confirm`, { validated, comment });
}

export async function completeTicket(
  id: string,
  response: string,
  workCarriedOut: string,
  workDuration: string,
) {
  return axios.post(`/api/tm/tickets/${id}/complete`, {
    response,
    actualMaintenanceTime: workDuration,
    workCarriedOut,
  });
}

// SOP
type SOPBase = {
  uuid: string;
  name: string;
  description: string;
  documents: Array<object>;
};

export type TodoList = {
  name: string;
  items: Array<TodoListItem>;
};

export type TodoListItem = {
  uuid: string;
  description: string;
  completionTracking: boolean;
  feedback: boolean;
  ordering: number;
  resources: Array<{
    resource: string;
    metadata: object;
  }>;
};

export type TodoListItemInstance = {
  completed: boolean;
  feedback: string;
  status: string;
  createdBy: string;
  createAt: string;
};

export type TodoListItemWithInstance = TodoListItem & {
  todoListUuid: string;
  instance: TodoListItemInstance;
};

export type TodoListWithInstance = {
  name: string;
  items: Array<TodoListItemWithInstance>;
};

export type SOPWithInstance = SOPBase & {
  sopRelationUuid: string;
  todoLists: Array<TodoListWithInstance>;
};

export type SOP = SOPBase & {
  todoLists: Array<TodoList>;
};

export type SOPQuery = {
  uuid?: string[];
  search?: string;
  name?: string;
  initial?: string;
  version?: number;
  twins?: string[];
  active?: boolean;
  created?: DateQuery;
  ticket?: string;
  page?: number;
  pageSize?: number;
};

export type TicketSOP = object;

export function getSops(
  query: SOPQuery,
  options?: AxiosRequestConfig,
): AxiosPromise<Paginated<SOP[]>> {
  return axios.post("/api/tm/sop/search", query, options);
}

export function addSopToTicket(ticketId: string, sopId: string, title: string) {
  return axios.post(`/api/tm/sop/twins`, {
    twin: ticketId,
    sop: sopId,
    title: title,
  });
}

export function removeSopFromTicket(id: string) {
  return axios.delete(`/api/tm/sop/twins/${id}`);
}

export type MaintenanceRule = {
  ticketCreationMode?: MaintenanceTypes.RecurrenceSettings["ticketCreationMode"];
  templateUuid: string;
  startDate: string;
  leadDuration?: string;
  name: string;
  description: string;
  type: "timespan" | "date";
  duration: string;
  properties: Array<{
    property: string;
    value: string | string[] | number;
    type: string;
  }>;
  sops: Array<{
    uuid: string;
    title: string;
  }>;
};

export type MaintenanceRuleContent = {
  name: string;
  type: string;
  description: string;
  rule: MaintenanceRule;
};

export type MaintenanceRuleResponse = {
  name: string;
  type: string;
  description: string;
  uuid: string;
  modified: string;
  modifiedBy: string;
  created: string;
  versions: Array<{
    uuid: string;
    ruleUuid: string;
    version: number;
    active: boolean;
    firstActivatedAt: string;
    modifiedBy: string;
    rule: MaintenanceRule;
  }>;
};

// NOTE: unfortunately request and response differ vastly here, thus they can't be used interchangeably
export function postMaintenanceRules(
  content: MaintenanceRuleContent,
): AxiosPromise<MaintenanceRuleResponse> {
  return axios.post("/api/tm/rules", content);
}

export type MaintenanceRulesQuery = {
  uuid?: string;
  type?: string;
  name?: string;
  active?: boolean;
  search?: string;
  created?: DateQuery;
  ticketUuid?: string;
  versionUuid?: string;
  responsibility?: string;
  page?: number;
  pageSize?: number;
};

export function getMaintenanceRules(
  query: MaintenanceRulesQuery,
  config?: AxiosRequestConfig,
): AxiosPromise<Paginated<MaintenanceRuleResponse[]>> {
  return axios.post(`/api/tm/rules/search`, query, config);
}

// NOTE:  The response value does not have the same structure as the request
export function patchMaintenanceRule(
  id: string,
  request: { rule: MaintenanceRule },
): AxiosPromise<MaintenanceRuleResponse> {
  return axios.patch(`/api/tm/rules/versions/${id}`, request);
}

export function activateMaintenanceRule(id: string) {
  return axios.post(`/api/tm/rules/versions/${id}/activate`);
}

export function deactivateMaintenanceRule(id: string) {
  return axios.post(`/api/tm/rules/versions/${id}/deactivate`);
}

// NOTE: This created a new VERSION of the maintenance rule, which then needs to be activated to be active, the return value is completely different from the request value
export function postMaintenanceRuleUpdate(
  id: string,
  request: MaintenanceRuleContent,
): AxiosPromise<MaintenanceRuleResponse> {
  return axios.post(`/api/tm/rules/${id}`, request);
}
export function deleteMaintenanceRule(
  id: string,
): AxiosPromise<MaintenanceRuleResponse> {
  return axios.delete(`/api/tm/rules/${id}`);
}

export type TodoInstance = {
  twinSop: string;
  todoListItemUuid: string;
  completed?: boolean;
  feedback?: string;
  status?: string;
};

export function patchTodoItemInstance(result: TodoInstance) {
  return axios.patch(`/api/tm/sop/todoinstance`, result);
}

export type TicketSop = {
  uuid: string;
  sopRelationUuid: string;
  name: string;
  description: string;
  title: string;

  documents: Array<{
    uuid: string;
    name: string;
    description: string;
  }>;
  todoLists: Array<{
    name: string;
    items: Array<{
      uuid: string;
      description: string;
      completionTracking: boolean;
      feedback: boolean;
      ordering: 0;
      resources: Array<{
        resource: string;
        metadata: object;
      }>;
      instance: {
        completed: boolean;
        feedback: string;
        status: string;
        createdBy: string;
        createdAt: string;
      };
    }>;
  }>;
};

export function getTicketSops(
  id: string,
): AxiosPromise<{ result: TicketSop[] }> {
  return axios.get(`/api/tm/tickets/${id}/sops`);
}

export type SOPCreateRequest = {
  name: string;
  description: string;
  type: string;
  domain: string;
  documents: Array<{ uuid: string }>;
  todoLists: Array<any>;
};

export function postSop(request: SOPCreateRequest) {
  return axios.post(`/api/tm/sop`, request);
}

export function postSopVersion(id: string, request: SOPCreateRequest) {
  return axios.post(`/api/tm/sop/${id}`, request);
}

export function deleteSopVersion(id: string) {
  return axios.delete(`/api/tm/sop/${id}`);
}

export type TicketRecurrenceQuery = {
  uuid?: string;
  versionUuid?: string;
  ticketUuid?: string;
  rule?: string;
  created?: DateQuery;
  page?: number;
  pageSize?: number;
};

export type TicketRecurrenceResponse = {
  ruleVersion: string;
  ticket: string;
  uuid: string;
  modified: string;
  created: string;
};

export function getTicketRecurrences(
  query: TicketRecurrenceQuery,
): AxiosPromise<Paginated<TicketRecurrenceResponse[]>> {
  return axios.post(`/api/tm/rules/ticketRecurrences/search`, query);
}

export type TicketCalendarQuery = {
  startDate?: string;
  endDate?: string;
  properties?: Array<{
    customId: string;
    values: string[];
    type: string;
  }>;
  types?: string[];
};

export type TicketCalendar = {
  tickets: TwinResponse[];
  maintenancePlans: Array<{ issuingDates: string[] } & MaintenanceRuleResponse>;
};

export function getTicketCalendar(
  query: TicketCalendarQuery,
): AxiosPromise<TicketCalendar> {
  return axios.post(`/api/tm/search/calendar`, query);
}
